import React from "react";
import { useParams, Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import CTABand from "@/components/CTABand";
import ProjectCard from "@/components/ProjectCard";
import { WHATSAPP_URL } from "@/components/WhatsAppButton";
import { destinations } from "@/data/destinations";
import { allProjects } from "@/data/projects";
import NotFound from "@/pages/NotFound";

const DestinationDetail = () => {
  const { slug } = useParams();
  const dest = destinations.find((d) => d.slug === slug);
  if (!dest) return <NotFound />;

  const related = allProjects.filter((p) => p.emirate === dest.name || (dest.slug === "ras-al-khaimah" && p.emirate === "Ras Al Khaimah") || (dest.slug === "dubai" && p.emirate === "Dubai") || (dest.slug === "abu-dhabi" && p.emirate === "Abu Dhabi"));
  const isHub = dest.slug === "ras-al-khaimah";
  const shown = isHub ? related : related.slice(0, 3);

  return (
    <div data-testid={`destination-page-${slug}`}>
      <SEO
        title={dest.metaTitle}
        description={dest.metaDescription}
        path={`/destinations/${slug}`}
        keywords={dest.keywords}
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Destinations", path: "/destinations" },
          { name: dest.name, path: `/destinations/${slug}` },
        ]}
      />
      <PageHero eyebrow="Destination" title={dest.h1 || dest.title} intro={dest.intro} introExtra={dest.introSeo} image={dest.image} testid={`destination-hero-${slug}`} />

      <section className="pb-20" data-testid="destination-lifestyle">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-5 gap-14">
          <div className="lg:col-span-3">
            <Reveal>
              <p className="eyebrow mb-4">The Lifestyle</p>
              <h2 className="font-display text-3xl sm:text-4xl font-light text-[#F4F3EF] mb-8 leading-tight">First, the life itself</h2>
            </Reveal>
            {dest.lifestyle.map((p, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <p className="text-[#9DA3AE] font-light leading-relaxed mb-6 text-lg">{p}</p>
              </Reveal>
            ))}
            <Reveal>
              <p className="eyebrow mb-4 mt-14">The Investment Logic</p>
              <h2 className="font-display text-3xl sm:text-4xl font-light text-[#F4F3EF] mb-8 leading-tight">Then, the case for ownership</h2>
            </Reveal>
            {dest.investment.map((p, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <p className="text-[#9DA3AE] font-light leading-relaxed mb-6 text-lg">{p}</p>
              </Reveal>
            ))}
          </div>
          <div className="lg:col-span-2">
            <Reveal delay={0.1}>
              <div className="lg:sticky lg:top-28 bg-[#13151D] border border-[rgba(197,168,128,0.2)] rounded-2xl p-8" data-testid="destination-highlights">
                <p className="eyebrow mb-6">Why {dest.name}</p>
                <ul className="space-y-4">
                  {dest.highlights.map((h) => (
                    <li key={h} className="flex gap-3 text-sm text-[#c9cdd6] font-light leading-relaxed">
                      <Check size={16} className="text-[#E6CA65] shrink-0 mt-0.5" /> {h}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/private-consultation"
                  data-testid="destination-consultation-cta"
                  className="mt-8 w-full inline-flex items-center justify-center gap-2 text-xs uppercase tracking-[0.14em] font-semibold px-6 py-4 rounded-full text-[#0B0C10] bg-gradient-to-r from-[#E6CA65] via-[#C5A880] to-[#9A7B4F] hover:shadow-[0_0_24px_rgba(197,168,128,0.45)] transition-shadow duration-300"
                >
                  Discuss {dest.name} Privately <ArrowRight size={14} />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {dest.seoSections && (
        <section className="pb-20" data-testid="destination-seo-sections">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="gold-hairline mb-14" />
            <div className="grid md:grid-cols-2 gap-x-14 gap-y-12">
              {dest.seoSections.map((s, i) => (
                <Reveal key={s.h2} delay={(i % 2) * 0.1}>
                  <div>
                    <h2 className="font-display text-2xl sm:text-3xl font-light text-[#F4F3EF] mb-4">{s.h2}</h2>
                    {s.text && <p className="text-[#9DA3AE] font-light leading-relaxed">{s.text}</p>}
                    {(s.paragraphs || []).map((p, pi) => (
                      <p key={pi} className="text-[#9DA3AE] font-light leading-relaxed mb-4">{p}</p>
                    ))}
                    {s.list && (
                      <ul className="space-y-3 mt-1">
                        {s.list.map((item, li) => (
                          <li key={li} className="flex gap-3 text-sm text-[#9DA3AE] font-light leading-relaxed">
                            <span className="w-1.5 h-1.5 rotate-45 bg-[#C5A880] mt-2 shrink-0" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {s.links && (
                      <div className="flex flex-wrap gap-2.5 mt-5">
                        {s.links.map((l) => (
                          <Link
                            key={l.to}
                            to={l.to}
                            data-testid={`seo-link-${l.to.replace(/\//g, "-")}`}
                            className="text-[11px] uppercase tracking-[0.15em] text-[#E6CA65] border border-[rgba(197,168,128,0.35)] rounded-full px-4 py-2 hover:bg-[rgba(197,168,128,0.1)] hover:border-[#C5A880] transition-colors duration-300"
                          >
                            {l.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {shown.length > 0 && (
        <section className="pb-24" data-testid="destination-related-projects">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <Reveal>
              <p className="eyebrow mb-4">In {dest.name}</p>
              <h2 className="font-display text-3xl sm:text-4xl font-light text-[#F4F3EF] mb-10">
                {isHub ? "Current Ras Al Khaimah projects" : "Curated opportunities here"}
              </h2>
            </Reveal>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {shown.map((p, i) => (
                <Reveal key={p.slug} delay={(i % 3) * 0.08}>
                  <ProjectCard project={p} index={i} />
                </Reveal>
              ))}
            </div>
            {isHub && (
              <Reveal>
                <div className="mt-12 bg-[#13151D] border border-[rgba(197,168,128,0.25)] rounded-2xl p-8 sm:p-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6" data-testid="price-list-cta">
                  <div>
                    <h3 className="font-display text-2xl text-[#F4F3EF] mb-2">Request the latest Al Marjan Island price list</h3>
                    <p className="text-sm text-[#9DA3AE] font-light max-w-lg">
                      Prices and availability change frequently. We will send the current comparison across live projects.
                    </p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3 shrink-0">
                    <Link
                      to="/private-consultation"
                      data-testid="price-list-cta-button"
                      className="inline-flex items-center justify-center gap-2 text-xs uppercase tracking-[0.14em] font-semibold px-7 py-3.5 rounded-full text-[#0B0C10] bg-gradient-to-r from-[#E6CA65] via-[#C5A880] to-[#9A7B4F] hover:shadow-[0_0_24px_rgba(197,168,128,0.45)] transition-shadow duration-300"
                    >
                      Get Latest Price List
                    </Link>
                    <a
                      href={WHATSAPP_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="price-list-cta-whatsapp"
                      className="inline-flex items-center justify-center gap-2 text-xs uppercase tracking-[0.14em] font-semibold px-7 py-3.5 rounded-full border border-[rgba(197,168,128,0.4)] text-[#E6CA65] hover:bg-[rgba(197,168,128,0.08)] transition-colors duration-300"
                    >
                      WhatsApp ACRE
                    </a>
                  </div>
                </div>
              </Reveal>
            )}
          </div>
        </section>
      )}

      {isHub ? (
        <section className="relative py-24 overflow-hidden grain" data-testid="destination-cta">
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B0C10] via-[#0E382B]/25 to-[#0B0C10]" />
          <div className="relative max-w-4xl mx-auto px-6 text-center">
            <Reveal>
              <p className="eyebrow mb-5">Private Consultation</p>
              <h2 className="font-display text-4xl sm:text-5xl font-light text-[#F4F3EF] leading-tight mb-6">Compare Al Marjan Island Projects</h2>
              <p className="text-[#9DA3AE] font-light max-w-xl mx-auto mb-10">
                Tell us your budget, preferred unit type and objective. ACRE can shortlist suitable current
                opportunities across Al Marjan Island.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="destination-cta-whatsapp"
                  className="inline-flex items-center justify-center gap-2 text-sm uppercase tracking-[0.14em] font-semibold px-8 py-4 rounded-full text-[#0B0C10] bg-gradient-to-r from-[#E6CA65] via-[#C5A880] to-[#9A7B4F] hover:shadow-[0_0_28px_rgba(197,168,128,0.5)] transition-shadow duration-300"
                >
                  WhatsApp ACRE
                </a>
                <Link
                  to="/private-consultation"
                  data-testid="destination-cta-availability"
                  className="inline-flex items-center justify-center gap-2 text-sm uppercase tracking-[0.14em] font-semibold px-8 py-4 rounded-full border border-[rgba(197,168,128,0.4)] text-[#E6CA65] hover:bg-[rgba(197,168,128,0.08)] transition-colors duration-300"
                >
                  Request Current Availability
                </Link>
                <Link
                  to="/private-consultation"
                  data-testid="destination-cta-consultation-button"
                  className="inline-flex items-center justify-center gap-2 text-sm uppercase tracking-[0.14em] font-semibold px-8 py-4 rounded-full border border-[rgba(244,243,239,0.25)] text-[#F4F3EF] hover:border-[#E6CA65] hover:text-[#E6CA65] transition-colors duration-300"
                >
                  Book a Private Consultation
                </Link>
              </div>
            </Reveal>
          </div>
        </section>
      ) : (
        <CTABand testid="destination-cta" />
      )}
    </div>
  );
};

export default DestinationDetail;
